import { React, ts, useSelector } from '_core';

import Chart from './chart';
import Form from './form';
import { DraftParams, INTERACTION_PARAMS, Params } from './types';

type PredictedAndRealizedRiskVsTimeProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  mode: ts.enums.CHART_MODE_ENUM;
  params: Params | DraftParams;
  setParams: (_p: DraftParams) => void;

  fullScreen?: boolean;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  widgetPaths?: Record<'backtest_realized_predicted_forecast' | 'portfolio_realized_predicted_forecast', string>;

  title: string;

  context:
    | ts.types.portfolio.PortfolioAnalysisContext
    | ts.types.backtest.BacktestAnaylsisContext
    | ts.types.signal.SignalAnalysisContext;
};

const PredictedAndRealizedRiskVsTime = ({
  id,
  readOnly,
  widgetKey,
  mode,
  params,
  setParams,
  loadWidgetData,
  widgetPaths,
  fullScreen,
  title,
  context,
}: PredictedAndRealizedRiskVsTimeProps) => {
  const fileKey = React.useMemo(
    () =>
      Object.keys(widgetPaths || {}).find((k) =>
        k.endsWith('_realized_predicted_forecast')
      ) as keyof typeof widgetPaths,
    [widgetPaths]
  );

  const strategies = useSelector((state) => state.resources.strategies);

  const riskModels = useSelector((state) => state.resources.risk_models);

  const strategy = React.useMemo(() => {
    return params[INTERACTION_PARAMS.IS_PORTFOLIO]
      ? strategies.find((s) => s.id === (context as ts.types.portfolio.PortfolioAnalysisContext).definition.strategy_id)
      : undefined;
  }, [strategies, context, params]);

  const riskModelId = React.useMemo(() => {
    if (strategy) return strategy.definition.risk_model_id;
    if (params[INTERACTION_PARAMS.IS_SIGNAL]) return (context as ts.types.signal.SignalAnalysisContext).risk_model_id;
    return (context as ts.types.backtest.BacktestAnaylsisContext).definition.risk_model_id;
  }, [strategy, context]);

  const riskModel = React.useMemo(() => {
    return riskModels.find((rm) => rm.id === riskModelId);
  }, [riskModels, riskModelId]);

  const renderJobComponent = () => (
    <Chart
      id={id}
      readOnly={readOnly}
      widgetKey={widgetKey}
      loadWidgetData={loadWidgetData}
      file={widgetPaths[fileKey]}
      fullScreen={fullScreen}
      title={title}
      riskModel={riskModel}
    />
  );

  return <>{mode == 'edit' ? <Form params={params} setParams={setParams} /> : renderJobComponent()}</>;
};

export default PredictedAndRealizedRiskVsTime;
